<template>
    <div
        class="quick-search"
        :class="[
            isMobile ? 'quick-search--mobile' : 'quick-search--desktop',
            {
                'quick-search--permanent': isPermanent,
                active: isExpanded,
                'non-empty': hasSearchText,
                'default-autocomplete': showDefaultTips,
            },
        ]"
    >
        <div class="quick-search__box">
            <input
                ref="quickSearchInput"
                id="quick-search-input"
                type="search"
                autocomplete="off"
                class="quick-search__input"
                placeholder="Vyhledávání ..."
                maxlength="32"
                :value="searchText"
                role="combobox"
                :aria-expanded="showAutocomplete"
                aria-haspopup="listbox"
                aria-controls="quick-search-listbox"
                :aria-activedescendant="selectedTipIndex >= 0 ? `search-tip-${selectedTipIndex}` : undefined"
                @input="handleInputChange"
                @keydown="handleKeyDown"
                @click="handleInputClick"
            />
            <div v-show="showAutocomplete" class="quick-search__autocomplete-container">
                <ul id="quick-search-listbox" class="quick-search__autocomplete" role="listbox">
                    <li
                        class="quick-search__autocomplete-item"
                        v-for="(tip, index) in searchTips"
                        :key="index"
                        role="option"
                        :id="`search-tip-${index}`"
                        :aria-selected="selectedTipIndex === index"
                        :aria-posinset="index + 1"
                        :aria-setsize="searchTips.length"
                    >
                        <QuickSearchTip :tip="tip" :is-selected="selectedTipIndex === index" />
                    </li>
                </ul>
                <a :href="searchUrl" class="quick-search__button">
                    <span>Zobrazit všechny výsledky</span>
                    <DsSystemIcon :size="20" class="quick-search__button-icon" name="arrow-right" />
                </a>
            </div>
        </div>
        <button type="button" class="quick-search__submit" aria-label="Hledat" @click="onSubmit">
            <DsSystemIcon :size="24" class="quick-search__submit-icon" name="search" />
        </button>

        <!-- Screen reader announcements -->
        <div aria-live="polite" aria-atomic="true" class="sr-only">
            {{ searchResultsAnnouncement }}
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, useTemplateRef, watch } from "vue";
import DsSystemIcon from "@sazka/design-system/src/icon/ds-system-icon.vue";
import QuickSearchTip from "./quick-search-tip.vue";
import { useKeyboardNavigation } from "../composables/use-keyboard-navigation";
import { useQuickSearch } from "../composables/use-quick-search";

const props = withDefaults(
    defineProps<{
        isMobile?: boolean;
    }>(),
    { isMobile: false }
);

const quickSearchRef = useTemplateRef("quickSearchInput");
const selectedTipIndex = ref(-1);

const {
    searchText,
    searchTips,
    isActive,
    showDefaultTips,
    isSearchResultPage,
    hasSearchText,
    showAutocomplete,
    searchUrl,
    onInput,
    onSubmit,
    onTipClick,
    showDefaultResults,
    activate,
    deactivate,
    hideAutocomplete,
    getTipUrl,
} = useQuickSearch();

const isPermanent = computed(() => isSearchResultPage || props.isMobile);
const isExpanded = computed(() => isActive.value || isPermanent.value);

const { handleKeyDown } = useKeyboardNavigation({
    selectedIndex: selectedTipIndex,
    itemsLength: computed(() => searchTips.value.length),
    showAutocomplete,
    searchText,
    searchTips,
    onInput,
    onSubmit,
    onTipClick,
    hideAutocomplete,
    deactivate,
    getTipUrl,
});

function getResultsLabel(count: number): string {
    if (count === 1) return "výsledek";
    if (count < 5) return "výsledky";
    return "výsledků";
}

const searchResultsAnnouncement = computed(() => {
    if (!showAutocomplete.value) return "";
    const count = searchTips.value.length;
    if (count === 0) return "Žádné výsledky";
    return `${count} ${getResultsLabel(count)} k dispozici`;
});

function handleInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    selectedTipIndex.value = -1;
    onInput(target.value);
}

function handleInputClick() {
    selectedTipIndex.value = -1;

    if (!isActive.value) {
        activate();
    }

    if (!searchText.value) {
        showDefaultResults();
    }
}

watch(isActive, (newActive) => {
    if (newActive && quickSearchRef.value) {
        quickSearchRef.value.focus();
    }
});

watch(searchTips, (newTips, oldTips) => {
    // Preserve selection if possible
    if (selectedTipIndex.value >= newTips.length) {
        selectedTipIndex.value = Math.max(0, newTips.length - 1);
    }

    // Reset only if tips completely changed
    if (oldTips?.length === 0 && newTips.length > 0) {
        selectedTipIndex.value = -1;
    }
});
</script>
